import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { campaignSelectors } from 'store/features/campaign/selectors'
import { formatDiscountCountdown } from 'utils/discount'

interface IUseDiscountCountdownResult {
  isDiscountActive: boolean
  countdownLabel: string | null
  buttonLabel: string | null
}

// Drives the header discount button from the active campaign (fetched from
// the campaign API via the notifications endpoint or a socket push — see
// useCampaignListener). The label comes from its discountPercent; a
// campaign without an endDate stays active without a countdown, an expired
// endDate deactivates the discount.
export const useDiscountCountdown = (): IUseDiscountCountdownResult => {
  const campaign = useSelector(campaignSelectors.campaign)
  const [now, setNow] = useState(() => Date.now())

  const endDate = campaign?.endDate ?? campaign?.content?.endDate
  const expiresAt = endDate ? new Date(endDate).getTime() : null

  useEffect(() => {
    if (expiresAt === null || expiresAt <= Date.now()) return

    setNow(Date.now())

    const intervalId = window.setInterval(() => {
      setNow(Date.now())
      if (Date.now() >= expiresAt) window.clearInterval(intervalId)
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [expiresAt])

  const remainingMs = expiresAt === null ? null : Math.max(0, expiresAt - now)

  return {
    // Campaign present + end date (when known) still in the future
    isDiscountActive:
      campaign !== null && (remainingMs === null || remainingMs > 0),
    countdownLabel:
      remainingMs !== null && remainingMs > 0
        ? formatDiscountCountdown(remainingMs)
        : null,
    buttonLabel: campaign?.discountPercent
      ? `${campaign.discountPercent}% OFF`
      : null
  }
}
